/*
 * HEADER CART — the cart drawer in the header, for every theme.
 * Mounted on #header-cart-container; the theme's markup can set:
 *   data-delimiters="[[,]]"        Vue delimiters when {{ }} is taken (Twig)
 *   data-open-on-add="1"           open the drawer after adding (instead of the short "added" toast)
 *   data-new-first="1"             the added item goes to the top of the list
 *   data-free-shipping-over="N"    free delivery above this amount (the sale channel's, from the engine)
 * Product name / image for a direct add: .pn-{id} / .pi-{id}, or the product
 * page contract ([data-pdp-name], [data-pdp-gallery="stage"] img).
 */
const cartOptions = () => {
    const el = document.getElementById('header-cart-container');
    const d = (el && el.dataset) || {};
    const over = Number(d.freeShippingOver);
    return {
        openOnAdd: d.openOnAdd === '1',
        newFirst: d.newFirst === '1',
        // no threshold (empty or 0) = no free-shipping bar
        freeShippingOver: d.freeShippingOver !== undefined && d.freeShippingOver !== '' && !isNaN(over) && over > 0 ? over : null,
        delimiters: d.delimiters ? d.delimiters.split(',') : null,
    };
};

/* The drawer's motion: open / close / drag helpers. The motion itself is CSS (the theme's `.show` transition on
   .vv-cart-panel); here only the timing and the gesture. */
const cartReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const cartIsSheet = () => window.matchMedia('(max-width: 47.999em)').matches;

/* scroll lock for the page behind: on iOS the <body> alone is not enough, the <html> needs it too */
const cartLock = (on) => {
    document.documentElement.classList.toggle('vv-cart-open', on);
    document.body.classList.toggle('vv-cart-open', on);
};

/* the END of the panel's transform transition: transitionend with a safety timer (background tab,
   interrupted transition); one wait at a time */
const cartMotion = {
    timer: null,
    panel: null,
    onEnd: null,
    cancel() {
        clearTimeout(this.timer);
        if (this.panel && this.onEnd) this.panel.removeEventListener('transitionend', this.onEnd);
        this.timer = this.panel = this.onEnd = null;
    },
    after(panel, cb) {
        this.cancel();
        const finish = () => { this.cancel(); cb(); };
        if (cartReduced()) { this.timer = setTimeout(finish, 0); return; }
        this.panel = panel;
        this.onEnd = (e) => { if (e.target === panel && e.propertyName === 'transform') finish(); };
        panel.addEventListener('transitionend', this.onEnd);
        this.timer = setTimeout(finish, 700);
    },
};

/* phone: the sheet closes by dragging down at the handle and the header (`[data-cart-drag]` in the theme's
   markup). Past a quarter of its height, or on a quick flick, it closes; less and it springs back; upwards it
   resists; the scrim fades while dragging. Bound once. (Escape is the component's keydown handler.) */
const cartGestures = {
    bound: false,
    bind(vm, box) {
        if (this.bound || !box) return;
        const panel = box.querySelector('.vv-cart-panel');
        const scrim = box.querySelector('.vv-cart-scrim');
        if (!panel) return;
        this.bound = true;

        let active = false, y0 = 0, dy = 0, h = 1, samples = [];
        const dragging = (on) => {
            panel.classList.toggle('is-dragging', on);
            if (scrim) scrim.classList.toggle('is-dragging', on);
        };
        const down = (e) => {
            if (!vm.show || !cartIsSheet() || e.button > 0 || e.target.closest('button, a')) return;
            active = true;
            y0 = e.clientY;
            dy = 0;
            h = panel.offsetHeight || 1;
            samples = [{ y: e.clientY, t: e.timeStamp }];
            cartMotion.cancel();
            panel.style.willChange = 'transform';
            dragging(true);
            e.currentTarget.setPointerCapture(e.pointerId);
        };
        const move = (e) => {
            if (!active) return;
            const raw = e.clientY - y0;
            /* follows the finger down, resists upwards (~24px at most) */
            dy = raw > 0 ? raw : -24 * (1 - Math.exp(raw / 80));
            samples.push({ y: e.clientY, t: e.timeStamp });
            while (samples.length > 2 && e.timeStamp - samples[0].t > 100) samples.shift();
            panel.style.setProperty('--vv-cart-drag', dy + 'px');
            if (scrim) scrim.style.opacity = String(Math.max(0, 1 - Math.max(0, dy) / h));
        };
        const up = () => {
            if (!active) return;
            active = false;
            dragging(false);
            if (scrim) scrim.style.opacity = '';
            const a = samples[0], b = samples[samples.length - 1];
            const v = (b.y - a.y) / Math.max(1, b.t - a.t);   /* px/ms over the last 100 ms */
            if (dy > h * .25 || (dy > 24 && v > .5)) { vm.close(); return; }
            panel.style.setProperty('--vv-cart-drag', '0px');
            cartMotion.after(panel, () => {
                panel.style.willChange = '';
                panel.style.removeProperty('--vv-cart-drag');
            });
        };
        box.querySelectorAll('[data-cart-drag]').forEach((el) => {
            el.addEventListener('pointerdown', down);
            el.addEventListener('pointermove', move);
            el.addEventListener('pointerup', up);
            el.addEventListener('pointercancel', up);
        });
    },
};

const HeaderCart = {
    data() {
        return {
            items: [],
            modal: null,
            newItems: [],
            show: false,
            options: cartOptions(),
        };
    },
    computed: {
        localStorageList() {
            return JSON.parse(localStorage.getItem('cart'));
        },
        itemsNum() {
            return this.items.length;
        },
        // pieces, not lines
        itemCount() {
            return this.items.reduce((n, item) => n + (parseInt(item.quantity) || 0), 0);
        },
        hasFreeShipping() { return this.options.freeShippingOver !== null; },
        freeShipLeft() {
            if (!this.hasFreeShipping) return 0;
            const left = this.options.freeShippingOver - this.totalPrice;
            return left > 0 ? left : 0;
        },
        freeShipPct() {
            if (!this.hasFreeShipping) return 100;
            return Math.min(100, Math.max(4, Math.round((this.totalPrice / this.options.freeShippingOver) * 100)));
        },
        totalPrice() {
            let sum = 0;
            this.items.forEach((item) => {
                sum += item.quantity * item.price;
            });

            return sum;
        },
    },
    async mounted() {
        this.syncA11y();
        this.items = this.localStorageList || [];

        let check = false;
        for (const item of this.items) {
            if(item.currency !== currency) {
                check = true;
            }
        }
        if(!check){
            return;
        }

        setTimeout(() => {
            let ids = this.items.map((e) => e.id);
            fetch("/api/cart/check", {
                method: "POST",
                headers: http.headers(),
                body: JSON.stringify({ids: ids}),
            })
                .then((response) => response.json())
                .then((data) => {
                    for (const item of this.items) {
                        const fresh = data.items?.[item.id];
                        // an item the API does not know any more keeps its old values
                        if (fresh === undefined || fresh === null) continue;
                        item.price = fresh.price;
                        item.original_price = fresh.original_price;
                        // the cart is shared by every channel (/huf, /ron): the item takes the currency of the
                        // price it was just given, or it keeps the old channel's currency mark
                        item.currency = currency;
                    }
                    // saved, so the next page does not show the old currency / price again
                    this.updateLocalStorage();
                });
        },400);
    },
    methods: {
        /* Keyboard / screen reader state of the drawer, whatever the theme's markup: closed = inert (its controls are not in
           the tab order), open = a modal dialog (focus goes in, Tab stays in, Escape closes, focus returns to the button). */
        panelEl() { return document.querySelector('#cart-box [role="dialog"], #cart-box .vv-cart-panel'); },
        buttonEl() { return document.querySelector('#header-cart-container button.badge-wrap, #header-cart > button'); },
        syncA11y() {
            const panel = this.panelEl();
            const btn = this.buttonEl();
            if (panel) {
                panel.toggleAttribute('inert', !this.show);
                panel.setAttribute('aria-hidden', this.show ? 'false' : 'true');
                panel.setAttribute('aria-modal', 'true');
            }
            if (btn) {
                btn.setAttribute('aria-expanded', this.show ? 'true' : 'false');
                btn.setAttribute('aria-haspopup', 'dialog');
                btn.setAttribute('aria-controls', 'cart-box');
                if (!btn.getAttribute('aria-label') && btn.getAttribute('title')) btn.setAttribute('aria-label', btn.getAttribute('title'));
            }
        },
        focusables(panel) {
            return [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')]
                .filter((e) => e.offsetParent !== null || e === document.activeElement);
        },
        onKey(e) {
            if (!this.show) return;
            if (e.key === 'Escape') { e.preventDefault(); this.toggle(); return; }
            if (e.key !== 'Tab') return;
            const panel = this.panelEl();
            if (!panel) return;
            const list = this.focusables(panel);
            if (!list.length) return;
            const first = list[0], last = list[list.length - 1];
            if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
            else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        },
        // open / close: the panel's own transition runs, the box is hidden only when it has ended (so the closing
        // animation is not cut), and a reopen in between does not hide it
        toggle() {
            if (this.show) this.close(); else this.open();
        },
        open() {
            const box = document.getElementById('cart-box');
            const panel = box && box.querySelector('.vv-cart-panel');
            cartMotion.cancel();
            if (box) box.style.display = '';
            cartLock(true);
            if (panel) {
                panel.style.willChange = 'transform';
                // the closed start state must be computed, or there is nothing to animate from
                void panel.offsetWidth;
                cartMotion.after(panel, () => { panel.style.willChange = ''; });
            }
            this.show = true;
            cartGestures.bind(this, box);
        },
        close() {
            if (!this.show) return;
            const box = document.getElementById('cart-box');
            const panel = box && box.querySelector('.vv-cart-panel');
            this.show = false;
            if (!panel) { cartLock(false); return; }
            panel.style.willChange = 'transform';
            cartMotion.after(panel, () => {
                if (this.show) return; // reopened meanwhile
                panel.style.willChange = '';
                panel.style.removeProperty('--vv-cart-drag');
                box.style.display = 'none'; // closed: nothing paints the iOS bars
                cartLock(false);
            });
        },
        addBundleToCart() {
            let ids = event.currentTarget.dataset.group_ids.split("|");
            for (const id of ids) {
                console.log(`sizes-${id}`);
                let name = document.getElementsByClassName(`pn-${id}`)[0].innerText;
                let img = document.getElementsByClassName(`pi-${id}`)[0].src;

                const sizes = document.getElementsByClassName(`sizes-${id}`)[0].getElementsByTagName('input');
                let size = null;
                let variantId = null;
                let price = null;
                let originalPrice = null;
                let sku = null;
                let serial = null;
                for (const sz of sizes) {
                    if(sz.checked){
                        size = sz.value;
                        variantId = sz.dataset.id;
                        price = sz.dataset.price;
                        originalPrice = sz.dataset.original_price;
                        sku = sz.dataset.sku;
                        serial = sz.dataset.serial;
                        break;
                    }
                }
                this.addItem(variantId,price,event.currentTarget.dataset.quantity,size,name,img,"",sku,serial,originalPrice);
            }
        },
        addMixToCart() {
            const top = document.getElementsByClassName('bikini-top swiper-slide-active')[0]
            const bottom = document.getElementsByClassName('bikini-bottom swiper-slide-active')[0]
            if(top != null && bottom != null) {
                let topID = top.getAttribute('data-id'),
                    topPrice = top.getAttribute('data-price'),
                    topName = top.getAttribute('data-name'),
                    topImg = top.getAttribute('data-img'),
                    topSize = top.getAttribute('data-size');

                let bottomID = bottom.getAttribute('data-id'),
                    bottomPrice = bottom.getAttribute('data-price'),
                    bottomName = bottom.getAttribute('data-name'),
                    bottomImg = bottom.getAttribute('data-img'),
                    bottomSize = bottom.getAttribute('data-size');

                const _this = this;
                if (this.modal === null) {
                    const tag = document.createElement('script');
                    tag.src = "/js/modal.js";
                    tag.onload = () => {
                        this.modal = ModalApp.mount("#modal");
                        this.setModal(topID,topPrice,topName,topImg);
                        this.setModal2(bottomID,bottomPrice,bottomName,bottomImg);
                        forms.load();
                        this.modal.addbtn = function () {
                            _this.addItem(_this.modal.product1.id,_this.modal.product1.price,_this.modal.quantity,_this.modal.product1.size,_this.modal.product1.name,_this.modal.product1.image,_this.modal.product1.color,_this.modal.product1.sku,_this.modal.product1.serial);
                            _this.addItem(_this.modal.product2.id,_this.modal.product2.price,_this.modal.quantity,_this.modal.product2.size,_this.modal.product2.name,_this.modal.product2.image,_this.modal.product2.color,_this.modal.product2.sku,_this.modal.product2.serial);
                        }
                    }
                    document.getElementsByTagName('body')[0].appendChild(tag);
                }else{
                    this.setModal(topID,topPrice,topName,topImg,topSize);
                    this.setModal2(bottomID,bottomPrice,bottomName,bottomImg,bottomSize);
                    this.modal.addbtn = function () {
                        _this.addItem(_this.modal.product1.id,_this.modal.product1.price,_this.modal.quantity,_this.modal.product1.size,_this.modal.product1.name,_this.modal.product1.image,_this.modal.product1.color,_this.modal.product1.sku,_this.modal.product1.serial);
                        _this.addItem(_this.modal.product2.id,_this.modal.product2.price,_this.modal.quantity,_this.modal.product2.size,_this.modal.product2.name,_this.modal.product2.image,_this.modal.product2.color,_this.modal.product2.sku,_this.modal.product2.serial);
                    }
                }
            }
        },
        addItemClick(event) {
            if(window.HEADERCART.noShow){
                return;
            }
            let id = event.currentTarget.dataset.id;
            let price = event.currentTarget.dataset.price;
            let originalPrice = event.currentTarget.dataset.original_price;
            let sku = event.currentTarget.dataset.sku;
            let serial = event.currentTarget.dataset.serial;
            // a missing name / image must not stop the add
            let name = document.getElementsByClassName(`pn-${id}`)[0]?.innerText
                ?? document.querySelector('[data-pdp-name]')?.innerText ?? "";
            let img = document.getElementsByClassName(`pi-${id}`)[0]?.src
                ?? document.querySelector('[data-pdp-gallery="stage"] img')?.src ?? "";
            if(event.currentTarget.dataset.quantity === undefined) {
                if (this.modal === null) {
                    const tag = document.createElement('script');
                    tag.src = "/js/modal.js";
                    tag.onload = () => {
                        this.modal = ModalApp.mount("#modal");
                        this.setModal(id,price,name,img);
                        forms.load();
                        this.modal.addbtn = () => this.addItem(this.modal.product1.id,this.modal.product1.price,this.modal.quantity,this.modal.product1.size,this.modal.product1.name,this.modal.product1.image,this.modal.product1.color,this.modal.product1.sku,this.modal.product1.serial);
                    }
                    document.getElementsByTagName('body')[0].appendChild(tag);
                }else{
                    this.setModal(id,price,name,img);
                    this.modal.addbtn = () => this.addItem(this.modal.product1.id,this.modal.product1.price,this.modal.quantity,this.modal.product1.size,this.modal.product1.name,this.modal.product1.image,this.modal.product1.color,this.modal.product1.sku,this.modal.product1.serial);
                }
            }else{
                // a product with one size / colour has no picker for it
                const sizesEl = document.getElementsByClassName(`sizes`)[0];
                let size = null;
                for (const sz of (sizesEl ? sizesEl.getElementsByTagName('input') : [])) {
                    if(sz.checked){
                        size = sz.value;
                        break;
                    }
                }
                const colorEl = document.getElementById("color") ?? document.querySelector('[data-pdp-value="main"]');
                let color = colorEl ? colorEl.innerText : null;
                // the selected variant values with the characteristic types' names (the product page's data-pdp-value
                // markers, data-label = the type's name): the cart and the checkout list them as "Type: value"
                const attributes = [...document.querySelectorAll('[data-pdp-value]')]
                    .map((el) => ({ label: el.dataset.label || '', value: (el.innerText || '').trim() }))
                    .filter((a) => a.value);
                this.addItem(id,price,event.currentTarget.dataset.quantity,size,name,img,color,sku,serial,originalPrice,attributes.length ? attributes : null);
            }
        },
        setModal(id,price,name,img,size=null){
            this.modal.product2.id = null;
            this.modal.product1.id = id;
            this.modal.product1.name = name;
            this.modal.product1.price = price;
            this.modal.product1.image = img;
            this.modal.product1.size = size;
            this.modal.show = true;
        },
        setModal2(id,price,name,img,size=null){
            this.modal.product2.id = id;
            this.modal.product2.name = name;
            this.modal.product2.price = price;
            this.modal.product2.image = img;
            this.modal.product2.size = size;
        },
        addItem(id,price,quantity,size,name,img,color,sku,serial,originalPrice=null,attributes=null){
            let data = {};
            data.item_id = id;
            data.item_sku = sku;
            data.item_serial = serial;
            data.item_name = name;
            data.item_category = null;
            data.currency = currency;
            data.price = price;
            data.quantity = quantity;
            data.original_price = originalPrice;
            AppEvents.callEvent("ADD_TO_CART", data);

            const newItem = {
                id: id,
                name: name,
                price: price,
                currency: currency,
                image: img,
                quantity: parseInt(quantity),
                size: size,
                color: color,
                sku: sku,
                serial: serial,
                original_price: originalPrice,
                attributes: attributes,
                timeAdded: Date.now(),
            };

            const alreadyInCart = this.items.find((item) => item.id === newItem.id && item.size === newItem.size);

            const toast = !this.options.openOnAdd;
            if (alreadyInCart && alreadyInCart.quantity) {
                const alreadyInNewCart = this.newItems.findIndex((item) => item.id === newItem.id && item.size === newItem.size);
                if(toast && alreadyInNewCart < 0) {
                    this.newItems.push(alreadyInCart);
                }
                alreadyInCart.quantity = alreadyInCart.quantity -(-quantity);
                if (this.options.newFirst) {
                    this.items.splice(this.items.indexOf(alreadyInCart), 1);
                    this.items.unshift(alreadyInCart);
                }
            } else {
                if (toast) this.newItems.push(newItem);
                if (this.options.newFirst) this.items.unshift(newItem);
                else this.items.push(newItem);
            }

            if (toast) {
                setTimeout(() => {
                    this.newItems = [];
                },5500);
            }

            this.updateLocalStorage();

            if (this.options.openOnAdd) {
                // the list scrolls to the top so the new item shows, the drawer opens (stays open)
                this.$nextTick(() => {
                    const body = document.querySelector('#cart-box [data-cart-body]');
                    if (body) body.scrollTop = 0;
                });
                if (!this.show) this.toggle();
            }
        },
        // "32 990 Ft", the same as the /cart page
        formatPrice(price) {
            // the theme's price rules (config.json "money"), see basics/money.js
            return window.Money ? Money.format(price, currency) : price + ' ' + currency;
        },
        canDecrement(item) {
            return item.quantity > 1;
        },
        stepQuantity(i, delta) {
            const item = this.items[i];
            if (!item) return;
            const quantity = item.quantity + delta;
            if (quantity < 1) return;
            item.quantity = quantity;
            this.updateLocalStorage();
        },
        remove(i) {
            this.items.splice(i, 1);
            this.updateLocalStorage();
        },
        updateLocalStorage() {
            Cookies.setItem("cart", this.items.map((e) => e.id).join("|"));
            localStorage.setItem('cart', JSON.stringify(this.items));
        },
    },
    watch: {
        show(open) {
            this.syncA11y();
            if (open) {
                document.addEventListener('keydown', this.onKey);
                this.$nextTick(() => {
                    const panel = this.panelEl();
                    const target = panel && (panel.querySelector('.vv-cart-close, button') || panel);
                    if (target) target.focus({ preventScroll: true });
                });
            } else {
                document.removeEventListener('keydown', this.onKey);
                const panel = this.panelEl();
                const btn = this.buttonEl();
                if (btn && (!panel || panel.contains(document.activeElement) || document.activeElement === document.body)) btn.focus({ preventScroll: true });
            }
        },
        localStorageList() {
            this.items = this.localStorageList || [];
        },
    }
};


// Global cart object
const HEADERCART = {
    vue: null,
    noShow: false,
    setNoShow(val){
        this.noShow = val;
    },
    load() {
        if (this.vue) return;
        if (typeof Vue === "undefined") {
            setTimeout(() => this.load(), 200);
            return;
        }
        if(document.getElementById('header-cart-container')) {
            const app = Vue.createApp(HeaderCart);
            const delimiters = cartOptions().delimiters;
            if (delimiters) app.config.compilerOptions.delimiters = delimiters;
            this.vue = app.mount('#header-cart-container');
        }

        const buttons = document.getElementsByClassName('btn-add-cart');
        for (let button of buttons) {
            button.addEventListener('click', (e) => HEADERCART.vue.addItemClick(e));
        }
        const bundleButtons = document.getElementsByClassName('btn-bundle-cart');
        for (let button of bundleButtons) {
            button.addEventListener('click', (e) => HEADERCART.vue.addBundleToCart(e));
        }
        const mixButtons = document.getElementsByClassName('btn-mix-cart');
        for (let button of mixButtons) {
            button.addEventListener('click', (e) => HEADERCART.vue.addMixToCart());
        }
    },
};
window.HEADERCART = HEADERCART;

window.addEventListener('DOMContentLoaded', () => {
    HEADERCART.load();
});

if (document.readyState !== 'loading') {
    HEADERCART.load();
}
